import { useState, FormEvent } from 'react';
import PublicPageLayout from '@/components/feature/PublicPageLayout';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('company_alt');
    if (hp && String(hp).trim()) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') payload.append(key, String(value));
    });

    setStatus('submitting');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9qf9jsnlsngrm4lm09g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore parse errors */
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (
        !response.ok ||
        (parsed && parsed.code !== 'OK') ||
        serverMsg.toLowerCase().includes('spam')
      ) {
        setStatus('error');
        setErrorMsg(serverMsg || 'Erro ao enviar mensagem. Tente novamente.');
        return;
      }
      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setErrorMsg('Erro de conexão. Verifique sua internet e tente novamente.');
    }
  };

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contato - WZap PRO',
    description:
      'Entre em contato com a WZap PRO. Envie sua mensagem pelo formulário ou fale diretamente pelo email e WhatsApp.',
    url: 'https://wzappro.com/contact',
    mainEntity: {
      '@type': 'Organization',
      name: 'WZap PRO',
      email: 'nx3designer@gmail.com',
      telephone: '+5554996542326',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Tapejara',
        addressRegion: 'RS',
        addressCountry: 'BR',
      },
    },
  };

  return (
    <PublicPageLayout
      title="Contato | WZap PRO"
      description="Fale com a equipe WZap PRO. Envie sua dúvida, sugestão ou solicitação pelo formulário de contato, email ou WhatsApp. Resposta em até 24 horas úteis."
      schemaJsonLd={schemaJsonLd}
    >
      <article>
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
          <div className="grid md:grid-cols-5 gap-8 md:gap-10">
            {/* Contact Info */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-3">
                  Fale Conosco
                </h2>
                <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
                  Tem dúvidas sobre planos, precisa de ajuda com setup ou quer propor uma parceria? Nosso time responde em até 24 horas úteis.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider">Email</p>
                    <a href="mailto:nx3designer@gmail.com" className="text-sm font-medium text-foreground-800 dark:text-foreground-200 hover:text-primary-600 transition-colors">
                      nx3designer@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-lg text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider">WhatsApp</p>
                    <a href="https://wa.me/5554996542326" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground-800 dark:text-foreground-200 hover:text-primary-600 transition-colors">
                      +55 54 99654-2326
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider">Endereço</p>
                    <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">Tapejara, RS — Brasil</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-lg text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-500 dark:text-foreground-400 uppercase tracking-wider">CNPJ</p>
                    <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">07.273.736/0001-82</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-3">
              <form
                data-readdy-form="d9qf9jsnlsngrm4lm09g"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-semibold text-foreground-700 dark:text-foreground-300 mb-1.5">
                    Nome completo
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="Seu nome"
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-100 text-sm text-foreground-950 dark:text-foreground-100 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-xs font-semibold text-foreground-700 dark:text-foreground-300 mb-1.5">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="seu@email.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-100 text-sm text-foreground-950 dark:text-foreground-100 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-semibold text-foreground-700 dark:text-foreground-300 mb-1.5">
                    Assunto
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    required
                    placeholder="Dúvida sobre plano, suporte técnico, parceria..."
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-100 text-sm text-foreground-950 dark:text-foreground-100 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-semibold text-foreground-700 dark:text-foreground-300 mb-1.5">
                    Mensagem
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Descreva sua dúvida ou solicitação..."
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-100 text-sm text-foreground-950 dark:text-foreground-100 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all resize-none"
                  />
                  <p className="text-[11px] text-foreground-400 mt-1">Máximo 500 caracteres</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp-field"
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Enviar mensagem
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl">
                    <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                      <i className="ri-check-line" />
                      Mensagem enviada com sucesso! Responderemos em até 24 horas úteis.
                    </p>
                  </div>
                )}

                {status === 'error' && (
                  <div className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                    <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                      <i className="ri-error-warning-line" />
                      {errorMsg}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </article>
    </PublicPageLayout>
  );
}